// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import {useCollapsedMessageVisibility} from '@app/features/channel/components/CollapsedMessageVisibilityContext';
import {PreloadableUserPopout} from '@app/features/channel/components/PreloadableUserPopout';
import Channels from '@app/features/channel/state/Channels';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import {MarkdownContext} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import type {Message} from '@app/features/messaging/models/Message';
import MessageReferences, {MessageReferenceState} from '@app/features/messaging/state/MessageReferences';
import {goToMessage} from '@app/features/messaging/utils/MessageNavigator';
import LocalUserSpamOverride from '@app/features/moderation/state/LocalUserSpamOverride';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import styles from '@app/features/theme/styles/Message.module.css';
import {Avatar} from '@app/features/ui/components/Avatar';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {useLingui} from '@lingui/react/macro';
import {ArrowBendUpLeftIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';
export const ReplyPreview = observer(
	({
		message,
		channelId,
		animateEmoji,
		messageDisplayCompact,
	}: {
		message: Message;
		channelId: string;
		animateEmoji: boolean;
		messageDisplayCompact: boolean;
	}) => {
		const {t} = useLingui();
		const {message: referencedMessage, state: messageState} = MessageReferences.getMessageReference(
			message.messageReference?.channel_id ?? '',
			message.messageReference?.message_id ?? '',
		);
		const {isMessageRevealed} = useCollapsedMessageVisibility();
		const guildId = Channels.getChannel(channelId)!.guildId;
		const jumpToRepliedMessage = useCallback(() => {
			if (message.messageReference?.message_id) {
				goToMessage(message.channelId, message.messageReference.message_id);
			}
		}, [message.channelId, message.messageReference]);
		if (!message.messageReference) return null;
		if (messageState !== MessageReferenceState.LOADED || !referencedMessage) {
			return (
				<div className={clsx(styles.repliedMessage, messageDisplayCompact && styles.repliedMessageCompact)}>
					<div className={styles.repliedIconContainer}>
						<ArrowBendUpLeftIcon weight="bold" className={styles.repliedIcon} />
					</div>
					<button type="button" disabled className={clsx(styles.repliedTextPreview, styles.unstyled)} tabIndex={-1}>
						{messageState === MessageReferenceState.DELETED ? (
							<span className={styles.repliedItalic}>{t`Original message was deleted`}</span>
						) : (
							<span className={styles.repliedItalic}>{t`Original message failed to load`}</span>
						)}
					</button>
				</div>
			);
		}
		const isSpammerReply =
			referencedMessage.author.id !== Authentication.currentUserId &&
			LocalUserSpamOverride.isUserMarkedAsSpammer(referencedMessage.author.id, referencedMessage.author.flags);
		const isCollapsedReferenceRevealed = isMessageRevealed(referencedMessage);
		const hiddenReplyReason =
			referencedMessage.blocked && !isCollapsedReferenceRevealed
				? t`Reply hidden because the original author is blocked.`
				: isSpammerReply && !isCollapsedReferenceRevealed
					? t`Reply hidden because the original author is marked as a spammer.`
					: null;
		if (hiddenReplyReason) {
			return (
				<div className={clsx(styles.repliedMessage, messageDisplayCompact && styles.repliedMessageCompact)}>
					<div className={styles.repliedIconContainer}>
						<ArrowBendUpLeftIcon weight="bold" className={styles.repliedIcon} />
					</div>
					<button type="button" disabled className={clsx(styles.repliedTextPreview, styles.unstyled)} tabIndex={-1}>
						<span className={clsx(styles.repliedTextContent, styles.repliedItalic)}>{hiddenReplyReason}</span>
					</button>
				</div>
			);
		}
		return (
			<div className={clsx(styles.repliedMessage, messageDisplayCompact && styles.repliedMessageCompact)}>
				{!messageDisplayCompact ? (
					<PreloadableUserPopout
						user={referencedMessage.author}
						isWebhook={referencedMessage.webhookId != null}
						webhookId={referencedMessage.webhookId ?? undefined}
						guildId={guildId}
						channelId={channelId}
						message={referencedMessage}
						enableLongPressActions={true}
					>
						<Avatar
							user={referencedMessage.author}
							size={16}
							className={styles.repliedAvatar}
							guildId={guildId}
							data-user-id={referencedMessage.author.id}
							data-guild-id={guildId}
						/>
					</PreloadableUserPopout>
				) : (
					<div className={styles.repliedIconContainer}>
						<ArrowBendUpLeftIcon weight="bold" className={styles.repliedIcon} />
					</div>
				)}
				<PreloadableUserPopout
					user={referencedMessage.author}
					isWebhook={referencedMessage.webhookId != null}
					webhookId={referencedMessage.webhookId ?? undefined}
					guildId={guildId}
					channelId={channelId}
					message={referencedMessage}
					enableLongPressActions={true}
				>
					<span
						className={styles.repliedUsername}
						style={{
							color: GuildMembers.getMember(guildId ?? '', referencedMessage.author.id)?.getColorString(),
						}}
						data-user-id={referencedMessage.author.id}
						data-guild-id={guildId}
					>
						{message.mentions.some((mention) => mention.id === referencedMessage.author.id) && '@'}
						{NicknameUtils.getNickname(referencedMessage.author, guildId)}
					</span>
				</PreloadableUserPopout>
				<FocusRing offset={-2}>
					<button
						type="button"
						className={clsx(styles.repliedTextPreview, styles.unstyled)}
						onClick={jumpToRepliedMessage}
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								jumpToRepliedMessage();
							}
						}}
					>
						{referencedMessage.content ? (
							<span className={clsx(styles.repliedTextContent, markupStyles.markup)}>
								<SafeMarkdown
									content={referencedMessage.content}
									options={{
										context: MarkdownContext.RESTRICTED_INLINE_REPLY,
										messageId: referencedMessage.id,
										channelId,
										disableAnimatedEmoji: !animateEmoji,
									}}
								/>
							</span>
						) : (
							<span className={clsx(styles.repliedTextContent, styles.repliedItalic)}>
								{t`Message contains attached media`}
							</span>
						)}
					</button>
				</FocusRing>
			</div>
		);
	},
);
